import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Stack,
  Hide,
  Show,
} from '@chakra-ui/react';
import { ICONS } from '~/constants/icons';
import { useUser } from '~/hooks/useUser';
import { getStreet, getStreetName, isSold } from '@habity/services';
import { getMunicipalityPath } from '@habity/services/municipalities';
import { getAreaPath } from '@habity/services/areas';
import { NestedHome, HomePreferences, TODO } from '@habity/types';
import { useModalWithUrl } from '~/hooks/useModalWithUrl';
import { Breadcrumbs } from '~/components/ui/Breadcrumbs';
import { useFilters } from '~/hooks/useFilters';
import { BreadcrumbsBuilder } from '~/lib/breadcrumbs-builder';
import { ImageGrid } from '~/components/ui/images/Grid';
import { FeatureItems } from './FeatureItems';
import { HomeProfileCTAs } from './components/CTAs';
import { HomeImageCard } from '~/components/ui/cards/ImageCard';
import { AdminMenu } from './components/admin/AdminMenu';
import { PageHero } from '~/components/ui/page/hero';
import { HomeProfileLikeAndShare } from './components/LikeAndShare';
import { useEffect, useState } from 'react';
import { patchAPI } from '~/lib/api';
import { API_PATHS } from '@habity/constants';
import { revalidateHome } from '~/app/actions/revalidate';

const DEFAULT_GRID_PREFERENCE = 6;

type HomeProfileHeroProps = {
  home: NestedHome;
};

export const HomeProfileHero = ({ home }: HomeProfileHeroProps) => {
  const [gridLayoutPreference, _setGridLayoutPreference] = useState<number>(
    DEFAULT_GRID_PREFERENCE
  );

  const { handleOpenWithUrlUpdate } = useModalWithUrl(home);
  const { createSearchPathFromFilters } = useFilters();
  const { user } = useUser();

  const { address, status } = home || {};

  // Set preferences
  useEffect(() => {
    const { preferences: preferencesData } = home || {};

    // Add type-safety to preferences
    const preferences: HomePreferences | null =
      (preferencesData as HomePreferences) || null;

    // Set grid to DB preference
    if (preferences?.grid && gridLayoutPreference !== preferences.grid) {
      _setGridLayoutPreference(preferences.grid);
    }
  }, [home]);

  const setGridLayoutPreference = (size: number) => {
    // Instantly update local state without delay
    setGridLayoutPreferenceInLocalState(size);
    // Update asynchronously in DB and revalidate SSR page for next load
    setGridLayoutPreferenceInDatabase(size);
  };

  const setGridLayoutPreferenceInLocalState = (size: number) => {
    _setGridLayoutPreference(size);
  };

  const setGridLayoutPreferenceInDatabase = async (size: number) => {
    if (!home?.id) return;

    const newPreferences = home.preferences
      ? { ...(home.preferences as HomePreferences), grid: size }
      : { grid: size };

    await patchAPI(
      `${API_PATHS.PROVIDERS.SUPABASE.HOMES.PREFERENCES}/${home.id}`,
      JSON.stringify(newPreferences)
    );

    revalidateHome(home);
  };

  const { street, municipality, city, area, municipality_id } = address || {};
  const { show_address } = status || {};
  const cmsUrl = process.env.NEXT_PUBLIC_CMS_URL;

  // Determine whether to show area/municipality or city in the breadcrumb
  // Prioritize showing area and municipality if available, otherwise show city
  const hasAreaAndMunicipality = !!area && !!municipality_id && !!municipality;

  const breadcrumbs = new BreadcrumbsBuilder()
    .addSection({
      'aria-label': 'Bostäder',
      href: '/homes',
    })
    // Only add municipality if we're using area/municipality path and not city
    .addSection(
      hasAreaAndMunicipality
        ? {
          'aria-label': municipality,
          href: getMunicipalityPath(
            {
              id: municipality_id,
              name: municipality,
            } as TODO,
            { tab: 'homes' }
          ),
        }
        : null
    )
    // Only add area if we're using area/municipality path and not city
    .addSection(
      hasAreaAndMunicipality && area
        ? {
          'aria-label': area.name,
          href: getAreaPath(area, { tab: 'homes' }),
        }
        : null
    )
    // Only add city if we're not showing area/municipality
    .addSection(
      !hasAreaAndMunicipality && city
        ? {
          'aria-label': city,
          href: createSearchPathFromFilters({
            filters: { city },
            includeExistingFilters: false,
          }),
        }
        : null
    )
    .addSection(
      !!street && !!city && !!show_address
        ? {
          'aria-label': getStreetName(street),
          href: createSearchPathFromFilters({
            filters: { streets: [getStreetName(street)], city },
            includeExistingFilters: false,
          }),
          isCurrentPage: true,
        }
        : null
    )
    .build();

  return (
    <PageHero pt="4" containerProps={{ pb: { mobile: 5, desktop: 6 } }}>
      <HStack justifyContent="space-between" alignItems="start" gap="4">
        <Box pt="2px">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </Box>

        <AdminMenu
          home={home}
          user={user}
          cmsUrl={cmsUrl}
          gridLayoutPreference={gridLayoutPreference}
          setGridLayoutPreference={setGridLayoutPreference}
        />
      </HStack>

      {/* Hero info and images */}
      <Flex
        direction={{
          mobile: 'column-reverse', // NOTE: Remember to put components in reverse order due to this
          desktop: 'column',
        }}
        py={{ mobile: 6, desktop: 0 }}
      >
        {/* Info */}
        <Stack mt="6" gap="0">
          {/* Desktop: Street */}
          <Hide below="desktop">
            <Heading as="h1" size="4xl" mt={{ desktop: '14' }}>
              <HStack spacing="2">
                <span>{getStreet(home)}</span>
                {home?.status?.show_verified_badge && (
                  <Icon
                    as={ICONS.CLAIMED}
                    color="black"
                    title="Denna bostad har en komplett profil."
                  />
                )}
              </HStack>
            </Heading>
          </Hide>

          {/* Features and actions */}
          <Flex
            direction="row"
            justify="space-between"
            align="center"
            mt={{ mobile: 0, desktop: '6' }}
            mb={{ mobile: 0, desktop: '3.5' }}
          >
            <Stack spacing="4">
              <Show below="desktop">
                <Box mt="-12px" ml="-4px">
                  <HomeProfileLikeAndShare home={home} />
                </Box>
              </Show>

              <FeatureItems home={home} variant="bullet-separated" />
            </Stack>

            <Hide below="desktop">
              <HomeProfileCTAs home={home} />
            </Hide>
          </Flex>
        </Stack>

        {/* Images */}
        <Box mx={0}>
          <Hide below="desktop">
            <ImageGrid
              home={home}
              handleOpenGallery={() =>
                handleOpenWithUrlUpdate({ modalType: 'gallery', open: true })
              }
              gridLayoutPreference={gridLayoutPreference}
            />
          </Hide>
          <Show below="desktop">
            <HomeImageCard
              home={home}
              user={user ?? undefined}
              imageVariant="profile"
              aspectRatio={1 / 1.2}
              bottomRightCTA
              onClick={() => { }}
              onGalleryModalOpen={() =>
                handleOpenWithUrlUpdate({ modalType: 'gallery', open: true })
              }
              imageProps={{ w: '100%' }}
              borderTopRadius="3px"
              borderBottomRadius="3px"
              boxProps={{ px: '0', w: '100%' }}
              handleOpen={() => { }}
              pagination={{ enabled: false }}
              maxSlides={status?.name === 'sold' ? 5 : undefined}
              showInspectedBadge={!isSold(home)}
              showBiddingBadge={!isSold(home)}
              showStatusBadge={true}
              showNextViewingBadge={!isSold(home)}
            />
          </Show>
        </Box>

        {/* Desktop: Actions */}
        <Hide below="desktop">
          <Box mt="5">
            <HomeProfileLikeAndShare home={home} />
          </Box>
        </Hide>

        {/* Mobile: Street on top (lower here in DOM due to column-reverse) */}
        <Show below="desktop">
          <Stack mb="4">
            {/* Spacer */}
            <Box h="6" />
            <Heading as="h1" size="2xl">
              <HStack spacing="2">
                <span>{getStreet(home)}</span>
                {home?.status?.show_verified_badge && (
                  <Icon
                    as={ICONS.CLAIMED}
                    color="black"
                    title="Denna bostad har en komplett profil."
                  />
                )}
              </HStack>
            </Heading>
          </Stack>
        </Show>
      </Flex>
    </PageHero>
  );
};
